import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Dialog,
  Fab,
  Tooltip,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import {
  submitProductDevelopmentAPI,
  fetchFGStatusesAPI,
  updateProjectDetailsAPI,
  updateProjectStatusesAPI,
  updateProjectDocumentDeliverablesAPI,
  updateCampaignDetailsAPI
} from "../stepperAPI";
import ProductDetails from "./ProductDetails";
import StatusSelection from "./StatusSelection";
import DocumentDeliverables from "./DocumentDeliverables";
import AddCampaignID from "./AddCampaignID";
import {
  resolveCampaignConfigArrays,
  resolveCampaignBOMDetails
} from "../campaignConfigPayload";
import StyledDialog from "../../../../../../common/models/Dialog";
import { useStyles } from './ReviewAndAddProjectStyles';

// Step names from stepper - these should match exactly what's shown in the stepper
// Import from stepper to ensure consistency (excluding the last step which is "Review And Add Project")
const STEP_NAMES = [
  "Project Details",
  "Status Selection",
  "Document Deliverables",
  "Add Campaign ID",
];

const STORAGE_KEYS = {
  FORM_DATA: "productDevelopmentFormData",
  CURRENT_STEP: "productDevelopmentCurrentStep",
  COMPLETED_STEPS: "productDevelopmentCompletedSteps",
};

const ReviewAndAddProject = ({
  formData,
  classes,
  handleBack,
  handleSubmit,
  handleGoToStep,
  activeStep,
  updateStepData,
  markApiCallSuccessful,
  isEditMode = false,
  isEditCampaign = false,
}) => {
  const localClasses = useStyles();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const campaignSectionRef = useRef(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errorPopup, setErrorPopup] = useState({ open: false, message: "" });
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Section saves are synchronous now (UI-only), so this never flips to true —
  // it stays so the section buttons keep their existing disabled/spinner props.
  const [sectionSaving] = useState({
    productDetails: false,
    fgStatuses: false,
    documentDeliverables: false,
    campaignID: false,
  });
  // Track which sections have been changed/edited
  const [changedSections, setChangedSections] = useState({
    productDetails: false,
    fgStatuses: false,
    documentDeliverables: false,
    campaignID: false,
  });
  // Store original form data to compare changes
  const [originalFormData, setOriginalFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      return saved ? JSON.parse(saved) : formData;
    } catch {
      return formData;
    }
  });

  // Edit Project is a long single-page form (four sections plus the whole
  // tab/column configuration), so one control jumps to whichever end the user
  // is not at: scrolled to the top it offers the bottom (where Update Project
  // lives), and once scrolled down it offers the top. The page scrolls the
  // window here — the stepper's own navigation uses window.scrollTo too.
  const [scrolledDown, setScrolledDown] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolledDown(window.scrollY > 200);
    onScroll(); // set correctly on mount, e.g. when restoring a scrolled position
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScrollJump = () => {
    window.scrollTo({
      top: scrolledDown ? 0 : document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  // Auto-scroll to campaign section when editing campaign (but don't auto-enter edit mode)
  useEffect(() => {
    if (isEditCampaign) {
      // Just scroll to the section, don't set editingSection
      // User needs to click "EDIT" to proceed
      setTimeout(() => {
        campaignSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [isEditCampaign]);

  // Sync with sessionStorage on mount to ensure we have latest data
  useEffect(() => {
    try {
      const savedFormData = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      if (savedFormData) {
        const parsedData = JSON.parse(savedFormData);
        // Update originalFormData to track changes
        setOriginalFormData(parsedData);
      } else {
        // If no saved data, use formData prop as original
        setOriginalFormData(formData);
      }
    } catch (error) {
      // If error, use formData prop as original
      setOriginalFormData(formData);
    }
  }, []);


  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Get latest data from sessionStorage
      let latestData;
      try {
        const savedFormData = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
        latestData = savedFormData ? JSON.parse(savedFormData) : formData;
      } catch (storageError) {
        latestData = formData;
      }

      if (isEditMode) {
        // EDIT MODE: this button is the ONE place anything is persisted. Section
        // Save buttons only hold their edits in form state (handleSaveSection),
        // so every section the user touched is submitted here — each through its
        // own endpoint, since the backend has no combined one.
        //
        // The Tab/Column configuration is always submitted: it is edited inline
        // (no section Save to mark it pending) and configHasChanges gates this
        // button, so reaching here means it may well have changed.
        const pendingSections = ["productDetails", "fgStatuses", "documentDeliverables"]
          .filter((name) => changedSections[name]);

        // axios rejects on 4xx, so the useful part — the middleware's own
        // `error` / `message` — is on err.response.data, not err.message.
        // Reading only err.message reduced every failure to the useless
        // "Request failed with status code 400".
        // The nested `error` is read BEFORE the outer `message`: on a Corda
        // rejection updateProjectStatuses.js returns
        // { message: 'Error from Corda', error: <corda response> }, so taking
        // `message` first reported the useless "Error from Corda" and threw away
        // the only part that says what Corda actually objected to.
        const describeFailure = (err, res) => {
          const data = err?.response?.data ?? res;
          const nested = data?.error;
          const nestedDetail =
            typeof nested === "string"
              ? nested
              : nested?.errorMessage ||
                nested?.message ||
                (nested && JSON.stringify(nested));
          return (
            nestedDetail ||
            data?.errorDescription ||
            data?.message ||
            err?.message ||
            "update failed"
          );
        };

        const failures = [];
        for (const name of pendingSections) {
          try {
            const res = await submitSection(name, latestData);
            if (!res || !res.status) {
              failures.push(`${name}: ${describeFailure(null, res)}`);
            }
          } catch (err) {
            failures.push(`${name}: ${describeFailure(err, null)}`);
          }
        }
        if (failures.length > 0) {
          // Stop before touching the campaign so the user is told exactly which
          // section did not go through, rather than getting a blanket success.
          throw new Error(
            `Some sections could not be updated — ${failures.join("; ")}`
          );
        }

        // Campaign / Tab & Column configuration goes through the same builder as
        // every other section. It used to be built inline here as a second,
        // near-identical copy of submitSection's "campaignID" case — two payloads
        // for one endpoint, free to drift apart on the next field added.
        const response = await submitSection("campaignID", latestData);

        // Real endpoints signal success via `status` (see updateCampaignDetailsAPI).
        if (response && response.status) {
          console.log("✅ Update successful:", response);
          // Reset changed sections after successful update
          setChangedSections({
            productDetails: false,
            fgStatuses: false,
            documentDeliverables: false,
            campaignID: false,
          });
          // Saved - the Update button goes back to disabled until the user
          // makes another configuration change.
          setConfigHasChanges(false);
          // Update original form data
          setOriginalFormData(latestData);
          setShowSuccessPopup(true);
        } else {
          throw new Error(response?.message || "Failed to update campaign details");
        }
      } else {
        // ADD MODE: Submit new project
        console.log("=== ADD NEW PROJECT - SUBMIT PAYLOAD ===");
        console.log("Full Payload:", latestData);
        console.log("Payload Keys:", Object.keys(latestData));
        console.log("Product Details:", latestData.productDetails);
        console.log("Status Selection:", latestData.fgStatuses);
        console.log("Document Deliverables:", latestData.documentDeliverables);
        console.log("Campaign ID:", latestData.campaignID);
        console.log("Column Configurations:", latestData.campaignID?.columnConfigurations);
        console.log("=== END PAYLOAD ===");
        
        const response = await submitProductDevelopmentAPI(latestData);
        
        if (response && response.success) {
          console.log("✅ Submit successful:", response);
          setShowSuccessPopup(true);
        }
      }
    } catch (error) {
      console.error("❌ Error in handleSave:", error);
      // Surface the real failure instead of faking success so the user knows the
      // update did not go through.
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errorDescription ||
        error?.message ||
        (isEditMode
          ? "An error occurred while updating the project. Please try again."
          : "An error occurred while saving the project. Please try again.");
      setErrorPopup({ open: true, message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseErrorPopup = () => {
    setErrorPopup({ open: false, message: "" });
  };

  // Handler for OK button in success popup
  const handlePopupOk = () => {
    setIsRedirecting(true);
    setShowSuccessPopup(false);

    // Only after the user has seen and acknowledged the success popup,
    // proceed with navigation by calling handleSubmit
    if (typeof handleSubmit === 'function') {
      handleSubmit();
    }
  };

  const handleEdit = (stepIndex) => {
    // Capture current state as original when entering edit mode
    try {
      const saved = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      if (saved) {
        const parsedData = JSON.parse(saved);
        setOriginalFormData(parsedData);
      }
    } catch (error) {
      console.error("Error capturing original state:", error);
    }
    setEditingSection(stepIndex);
  };

  // Builds one section's payload, calls that section's own endpoint and returns
  // the response.
  //
  // Each section has a SEPARATE endpoint (project details / statuses / document
  // deliverables / campaign details) and the backend exposes no combined one, so
  // the single Update Project click fans out to only the sections the user
  // actually changed. A section's own Save button never reaches this — it is
  // UI-only (see handleSaveSection).
  const submitSection = async (stepName, latestData) => {
      // Extract IDs from formData
      const collabrixProjectID = latestData?.productDetails?.collabrixProjectID ||
                                  latestData?.collabrixProjectID || null;
      const collabrixCampaignID = latestData?.campaignID?.collabrixCampaignID || 
                                  latestData?.collabrixCampaignID || null;
      const cdmoOrgID = latestData?.productDetails?.cdmoOrgID || 
                        latestData?.cdmoOrgID || null;
      const sponsorOrgID = latestData?.productDetails?.sponsorOrgID || 
                           latestData?.sponsorOrgID || null;

      console.log(`📡 Calling API for section: ${stepName}`, {
        collabrixProjectID,
        collabrixCampaignID,
        cdmoOrgID,
        sponsorOrgID
      });

      let apiResponse = null;

      // Call respective API based on section
      switch (stepName) {
        case "productDetails": {
          const stepData = latestData?.productDetails || {};
          
          // Format dates properly (handle moment objects, Date objects, or strings)
          const formatDate = (dateValue) => {
            if (!dateValue) return null;
            if (dateValue.format) {
              // moment/dayjs object
              return dateValue.toISOString ? dateValue.toISOString() : dateValue.format("YYYY-MM-DD");
            }
            if (dateValue instanceof Date) {
              return dateValue.toISOString();
            }
            // Already a string, return as is
            return dateValue;
          };
          
          // Handle deliverableType: if it's an array, we need to convert to IDs
          // If deliverableTypeIDs already exists, use it; otherwise try to convert
          let deliverableTypeIDs = stepData.deliverableTypeIDs || "";
          if (!deliverableTypeIDs && Array.isArray(stepData.deliverableType) && stepData.deliverableType.length > 0) {
            // If we have deliverableType array but no IDs, we'd need the mapping
            // For now, log a warning - in production, you'd need access to deliverableTypes list
            console.warn("deliverableType array found but deliverableTypeIDs not available. Using empty string.");
          }
          
          // Build payload similar to ProductDetails component
          const payload = {
            collabrixProductID: stepData.collabrixProductID || null,
            collabrixProjectID: collabrixProjectID || null, // null for new, ID for update
            sponsorOrgID: sponsorOrgID,
            cdmoOrgList: cdmoOrgID ? [{ cdmoOrgID: cdmoOrgID }] : [],
            projectStartDate: formatDate(stepData.projectStartDate),
            projectEndDate: formatDate(stepData.projectEndDate),
            deliverableTypeIDs: deliverableTypeIDs,
            projectName: stepData.projectID || "",
            projectStatus: "",
            projectStatusID: null,
          };
          apiResponse = await updateProjectDetailsAPI(payload);
          
          // Update collabrixProjectID if returned from API (for new projects)
          if (apiResponse?.data?.collabrixProjectID && !collabrixProjectID) {
            const updatedData = { ...latestData };
            if (!updatedData.productDetails) updatedData.productDetails = {};
            updatedData.productDetails.collabrixProjectID = apiResponse.data.collabrixProjectID;
            updatedData.collabrixProjectID = apiResponse.data.collabrixProjectID;
            sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(updatedData));
          }
          break;
        }

        case "fgStatuses": {
          const stepData = latestData?.fgStatuses || {};

          // Mirrors StatusSelection.jsx's mapStatusesToAPI — the working
          // reference for this endpoint. The local copy that used to live here
          // sent `statusType` (updateProjectStatuses.js reads `statusObjectType`)
          // and omitted sponsorSelection / cmoSelection altogether, so the
          // middleware forwarded undefined selections to Corda and the call came
          // back 400.
          //
          // Per FSD §5 the selection is single for Project and Campaign, and
          // split BMS/CMO for the other four — so cmoSelection is sent only for
          // those, exactly as the reference does.
          //
          // Reads both status shapes in play: StatusSelection stores
          // value / valueBms / valueCmo once its section has been opened, while
          // an untouched section still holds the `activated` form that
          // ProductDevelopmentStepper mapped from the API. `??` (not `||`) so an
          // explicit false is respected instead of falling through.
          //
          // EVERY status is sent, selected or not — deliberately unlike the
          // reference, which filters to selected only. UpdateProjectStatusesFlow
          // upserts per statusID (setSponsorSelection/setCmoSelection from the
          // incoming values) and touches nothing that is omitted, so a status
          // left out simply keeps its old value: switching one off never
          // reached Corda, and on reopen — with no saved row to read — it fell
          // back to the seed/master default and appeared switched on again.
          // Sending false makes the deselection explicit and persistent.
          const mapStatusesToAPI = (statuses, statusObjectType, hasBmsCmo = false) => {
            if (!Array.isArray(statuses)) return [];
            const isSingleSelection =
              statusObjectType === "project" || statusObjectType === "campaign";
            // activated_bms / activated_cmo are the stepper's own BMS/CMO pair
            // (sponsorSelection / cmoSelection). They matter when a section was
            // never opened, so no valueBms/valueCmo exists yet — without them
            // both sides fell back to the collapsed `activated` and a status set
            // for CMO only would have been saved as set for both.
            const singleOf = (s) => (s.value ?? s.activated) === true;
            const bmsOf = (s) =>
              (s.valueBms ?? s.activated_bms ?? s.activated) === true;
            const cmoOf = (s) =>
              (s.valueCmo ?? s.activated_cmo ?? s.activated) === true;

            return statuses
              .filter((s) => (s.status_id ?? s.statusID) != null)
              .map((s) => {
                const mapped = {
                  statusID: s.status_id || s.statusID,
                  statusName: s.status_name || s.statusName || "",
                  sponsorSelection: hasBmsCmo ? bmsOf(s) : singleOf(s),
                  statusObjectType,
                };
                if (!isSingleSelection) {
                  mapped.cmoSelection = hasBmsCmo ? cmoOf(s) : singleOf(s);
                }
                return mapped;
              });
          };

          const allStatuses = [
            ...mapStatusesToAPI(stepData.projectStatuses || [], "project", false),
            ...mapStatusesToAPI(stepData.campaignStatuses || [], "campaign", false),
            ...mapStatusesToAPI(stepData.changeControlStatuses || [], "changeControl", true),
            ...mapStatusesToAPI(stepData.deviationStatuses || [], "deviation", true),
            ...mapStatusesToAPI(stepData.inProcessTestStatuses || [], "inProcessTest", true),
            ...mapStatusesToAPI(stepData.productReleaseTestStatuses || [], "productReleaseTest", true),
          ];

          console.log("=== updateProjectStatuses PAYLOAD ===", allStatuses);

          const payload = {
            collabrixProjectID: collabrixProjectID,
            cdmoOrgID: cdmoOrgID,
            statuses: allStatuses,
          };
          apiResponse = await updateProjectStatusesAPI(payload);
          break;
        }

        case "documentDeliverables": {
          const stepData = latestData?.documentDeliverables || {};
          const documents = stepData.documents || [];
          
          // Map documents to API format
          const documentDetailsList = documents
            .filter(doc => doc.documentTypeID && doc.fileFormatTypeID && doc.orgResponsibleID)
            .map(doc => ({
              documentTypeID: doc.documentTypeID,
              fileFormatID: doc.fileFormatTypeID,
              uploadOrgCollaboratorTypeID: doc.orgResponsibleID,
              documentCategoryID: doc.documentCategoryID || null,
              documentGroupID: doc.documentGroupID || null,
            }));

          const payload = {
            collabrixProjectID: collabrixProjectID,
            cdmoOrgID: cdmoOrgID,
            documentDetailsList: documentDetailsList,
          };
          apiResponse = await updateProjectDocumentDeliverablesAPI(payload);
          break;
        }

        case "campaignID": {
          const stepData = latestData?.campaignID || {};

          // Rebuild tab/column config from the live toggled state so edits are
          // actually persisted (toggles mutate tabConfiguration /
          // columnConfigurations, not the campaign*Configuration arrays).
          // Pass the resolved campaign id so every object is stamped with it.
          const { campaignTabConfiguration, campaignColumnConfiguration } =
            resolveCampaignConfigArrays(stepData, collabrixCampaignID);

          // Format dates properly (handle moment/dayjs objects, Date objects, or strings)
          const formatCampaignDate = (dateValue) => {
            if (!dateValue) return null;
            if (dateValue.format) {
              // moment/dayjs object
              return dateValue.format("YYYY-MM-DD");
            }
            if (dateValue instanceof Date) {
              return dateValue.toISOString().split('T')[0]; // YYYY-MM-DD format
            }
            // Already a string, return as is
            return dateValue;
          };
          
          // Get sponsorMosymphonyID (similar to AddCampaignID component)
          const sponsorMosymphonyID = sponsorOrgID || null;
          
          // Build payload similar to AddCampaignID component
          const payload = {
            campaignDetails: {
              collabrixProjectID: collabrixProjectID,
              collabrixCampaignID: collabrixCampaignID || null, // null for new, ID for update
              campaignID: stepData.campaignID || "",
              poNumber: stepData.poNumber || "",
              productQuantityRequired: stepData.productQuantity || "",
              uom: stepData.uom || "KG",
              viewType: stepData.defaultView || "System",
              campaignStartDate: formatCampaignDate(stepData.campaignStartDate),
              campaignEndDate: formatCampaignDate(stepData.campaignEndDate),
              manufacturingFacility: stepData.manufacturingFacility || "",
              dataGranularityID: stepData.dataGranularityID || null,
              cdmoOrgID: cdmoOrgID,
              numberOfStages: stepData.numberOfStages ? parseInt(stepData.numberOfStages) : null,
              campaignStatusID: stepData.campaignStatusID || null,
              campaignStatusText: stepData.campaignStatusText || null,
              sponsorMosymphonyID: sponsorMosymphonyID,
              vendorMosymphonyID: cdmoOrgID, // Assuming cdmoOrgID is the vendor ID
            },
            campaignStageDetails: stepData.campaignStageDetails || [],
            // Rebuilt from the live material tables, not the fetched array —
            // otherwise the Selection toggles never reach the payload.
            campaignBOMDetails: resolveCampaignBOMDetails(stepData, {
              collabrixCampaignID,
              collabrixProjectID,
            }),
            campaignTabConfiguration,
            campaignColumnConfiguration,
          };
          apiResponse = await updateCampaignDetailsAPI(payload);
          
          // Update collabrixCampaignID if returned from API (for new campaigns)
          if (apiResponse?.data?.collabrixCampaignID && !collabrixCampaignID) {
            const updatedData = { ...latestData };
            if (!updatedData.campaignID) updatedData.campaignID = {};
            updatedData.campaignID.collabrixCampaignID = apiResponse.data.collabrixCampaignID;
            updatedData.collabrixCampaignID = apiResponse.data.collabrixCampaignID;
            sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(updatedData));
          }
          break;
        }

        default:
          console.warn(`Unknown section: ${stepName}`);
      }

      return apiResponse;
  };

  // A section's Save button is UI-ONLY: it commits the edit into the form state
  // already held in sessionStorage, marks the section as pending, and closes the
  // section's edit mode. Nothing is sent to the backend here.
  //
  // Every change — statuses, document deliverables, product details, and the
  // Tab/Column configuration — is persisted by ONE action: the Update Project
  // button at the bottom (handleSave). Previously each section's Save fired its
  // own endpoint straight away, so changing statuses and then tabs issued two
  // separate updates before the user had even pressed Update, and a Discard
  // afterwards could not take back what had already been written.
  const handleSaveSection = (stepIndex, stepName) => {
    setChangedSections((prev) => {
      const updated = { ...prev, [stepName]: true };
      console.log(`💾 Section "${stepName}" held locally (not yet sent). Pending:`, updated);
      return updated;
    });

    // Re-baseline the "original" snapshot so this section's now-committed edit
    // is not reverted by a later Discard on a different section.
    try {
      const saved = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      if (saved) setOriginalFormData(JSON.parse(saved));
    } catch (error) {
      console.error(`Error re-baselining after section "${stepName}":`, error);
    }

    setEditingSection(null);
  };

  const handleCancelEdit = () => {
    // Cancel editing - exit edit mode without saving
    // Revert to original data
    try {
      if (originalFormData) {
        sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(originalFormData));

        // Push the reverted values back through updateStepData so React state
        // is reverted too. Writing sessionStorage and firing a 'storage' event
        // did neither: nothing listens for that event (it does not fire in the
        // page that called setItem anyway), so formData kept the edited values
        // and each section's own local copy of them stayed on screen — Discard
        // appeared to keep the edit instead of dropping it.
        const stepNames = [
          "productDetails",
          "fgStatuses",
          "documentDeliverables",
          "campaignID",
        ];
        const reverting = stepNames[editingSection];
        if (reverting && typeof updateStepData === "function") {
          const fallback = reverting === "documentDeliverables" ? [] : {};
          updateStepData(reverting, originalFormData[reverting] ?? fallback);
        }
      }
    } catch (error) {
      console.error("Error reverting changes:", error);
    }
    setEditingSection(null);
  };

  // Helper function to check if a section has changes
  const hasSectionChanges = (stepName) => {
    try {
      const currentData = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      if (!currentData || !originalFormData) return false;
      
      const current = JSON.parse(currentData);
      const original = originalFormData;
      
      // Compare section data
      if (stepName === "productDetails") {
        return JSON.stringify(current.productDetails || {}) !== JSON.stringify(original.productDetails || {});
      } else if (stepName === "fgStatuses") {
        return JSON.stringify(current.fgStatuses || {}) !== JSON.stringify(original.fgStatuses || {});
      } else if (stepName === "documentDeliverables") {
        return JSON.stringify(current.documentDeliverables || []) !== JSON.stringify(original.documentDeliverables || []);
      } else if (stepName === "campaignID") {
        return JSON.stringify(current.campaignID || {}) !== JSON.stringify(original.campaignID || {});
      }
      return false;
    } catch (error) {
      console.error("Error checking section changes:", error);
      return false;
    }
  };

  // Edit Project: true once the user has actually edited the Tab / Column
  // configuration. Gates the Update Project button (see saveDisabled below).
  const [configHasChanges, setConfigHasChanges] = useState(false);

  // Monitor formData changes to enable/disable save buttons
  const [sectionHasChanges, setSectionHasChanges] = useState({
    productDetails: false,
    fgStatuses: false,
    documentDeliverables: false,
    campaignID: false,
  });

  // Check for changes whenever editingSection changes - use interval to monitor real-time changes
  useEffect(() => {
    if (editingSection !== null) {
      const stepNames = ["productDetails", "fgStatuses", "documentDeliverables", "campaignID"];
      const stepIndex = editingSection;
      const stepName = stepNames[stepIndex];
      
      if (stepName) {
        // Check immediately
        const hasChanges = hasSectionChanges(stepName);
        setSectionHasChanges(prev => ({
          ...prev,
          [stepName]: hasChanges,
        }));
        
        // Set up interval to check for changes while editing
        const interval = setInterval(() => {
          const hasChangesNow = hasSectionChanges(stepName);
          setSectionHasChanges(prev => ({
            ...prev,
            [stepName]: hasChangesNow,
          }));
        }, 300); // Check every 300ms
        
        return () => clearInterval(interval);
      }
    } else {
      // Reset all changes when not editing
      setSectionHasChanges({
        productDetails: false,
        fgStatuses: false,
        documentDeliverables: false,
        campaignID: false,
      });
    }
  }, [editingSection, originalFormData]);

  /**
   * REVIEW AND EDIT PAGE - SAME COMPONENT FOR BOTH ADD AND EDIT MODES
   * 
   * This component is used for BOTH:
   * 1. Review page (from "Add New Project" flow - final step of stepper)
   * 2. Edit page (from "Edit Project" action - directly navigates to review page)
   * 
   * KEY BEHAVIOR (Same as Commercial Supply):
   * 
   * 1. **All Sections Read-Only by Default**: When the page loads, ALL sections display
   *    data but are NOT editable. Nothing is editable until user clicks "Edit" on a section.
   * 
   * 2. **Section-Level Editing**: When user clicks "Edit" on a section:
   *    - Only THAT section becomes editable (readOnly=false for that section)
   *    - All OTHER sections remain read-only
   *    - Edit buttons on other sections are disabled
   *    - Main "Save Project" button is disabled while editing
   * 
   * 3. **Reuse Existing Step Components**: Instead of duplicating review display logic,
   *    we reuse the same step components (ProductDetails, StatusSelection, etc.) that
   *    already have built-in read-only/view mode support.
   * 
   * 4. **Single Source of Truth**: The step components already know how to display
   *    their data in both edit and view modes. By reusing them, we ensure consistency
   *    - if a step component changes, the review page automatically reflects those changes.
   * 
   * 5. **No Back Buttons in Sections**: Using `hideStepActions={true}` hides the internal
   *    navigation buttons (Back/Next) that step components normally show. This is correct
   *    because in review page, sections are not part of a stepper flow - they're just
   *    review/edit sections. The main page has the back button for navigation.
   * 
   * 6. **Edit Mode State**: `editingSection` state tracks which section (if any) is
   *    currently being edited. When null, all sections are read-only. When set to a
   *    stepIndex, only that section is editable.
   */
  const getStepComponentProps = (stepIndex, stepName) => ({
    formData,
    // On Edit Project the only editable content is the Tab / Column
    // configuration, and every one of its toggles / adds / renames / deletes
    // goes through updateStepData("campaignID", ...). No initialisation effect
    // writes that section, so a write here is always a real user edit - which
    // makes this the reliable signal for "something changed". The Update
    // Project button stays disabled until it fires.
    updateStepData: (section, data) => {
      if ((isEditMode || isEditCampaign) && section === "campaignID") {
        setConfigHasChanges(true);
      }
      return updateStepData(section, data);
    },
    classes,
    handleNext: () => {},
    handleSubmit: () => {},
    activeStep: stepIndex,
    stepsLength: 5,
    markApiCallSuccessful,
    // CRITICAL: All sections are read-only by default until user clicks "Edit"
    // Only the section being edited (editingSection === stepIndex) becomes editable
    readOnly: editingSection !== stepIndex, 
    hideStepActions: true, // Hide back/next buttons - sections don't need navigation
    ...(stepName === "statusSelection" && { fetchFGStatusesAPI }),
  });


  // On the Edit Project / Edit Campaign screens the per-section EDIT buttons are
  // HIDDEN — every section is read-only. The only editable content is the Tab &
  // Column configuration, which is editable inline inside the Add Campaign ID
  // section (see configOnlyEditable passed to AddCampaignID below). The user
  // saves everything with the Update Project button.
  const disableEditing = isEditMode || isEditCampaign;
  // On Edit Project the Update button starts DISABLED and only becomes enabled
  // once the user actually changes the Tab / Column configuration (tracked by
  // configHasChanges, set from the updateStepData wrapper above). Previously it
  // was enabled from the moment the screen opened, inviting a no-op update.
  // A section Save no longer writes anything on its own, so it must be able to
  // enable this button too — otherwise a statuses-only edit could never be
  // submitted (configHasChanges only tracks the Tab/Column configuration).
  const hasPendingSections = Object.values(changedSections).some(Boolean);
  const saveDisabled =
    isSubmitting ||
    isRedirecting ||
    editingSection !== null ||
    (disableEditing && !configHasChanges && !hasPendingSections);

  return (
    <Box>
      {/* Jump-to-end control. Edit Project only: the add-mode review page keeps
          its existing stepper navigation. Label and direction follow the current
          scroll position. */}
      {(isEditMode || isEditCampaign) && (
        <Tooltip
          title={scrolledDown ? "Scroll to top" : "Go down"}
          placement="left"
        >
          <Fab
            size="small"
            onClick={handleScrollJump}
            aria-label={scrolledDown ? "Scroll to top" : "Go down"}
            style={{
              position: "fixed",
              right: "32px",
              bottom: "32px",
              zIndex: 1200,
              backgroundColor: "#ec6535",
              color: "#fff",
            }}
          >
            {scrolledDown ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </Fab>
        </Tooltip>
      )}

      {/* Hide title in edit mode - main header already shows "Edit Project" */}
      {/* In add mode, show "Project configuration done" */}
      {!isEditMode && (
        <Typography 
          variant="h6" 
          className={classes?.formTitle}
          style={{
            marginBottom: "24px",
          }}
        >
          Project configuration done
        </Typography>
      )}

      {/* Project Details Section */}
      <Paper elevation={0} style={{ padding: "24px", marginBottom: "24px", border: "1px solid #e0e0e0" }}>
        <Box className={localClasses.sectionHeader}>
          <Typography variant="h6" className={localClasses.sectionTitle}>
            {STEP_NAMES[0]}
          </Typography>
          {editingSection === 0 ? (
            <Box>
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                style={{
                  marginRight: "8px",
                  textTransform: "none",
                  color: "#424242",
                  borderColor: "#e0e0e0",
                  padding: "6px 16px",
                }}
                size="small"
                disabled={sectionSaving.productDetails}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSection(0, "productDetails")}
                disabled={!sectionHasChanges.productDetails || sectionSaving.productDetails}
                style={{
                  backgroundColor: sectionHasChanges.productDetails ? "#ec6535" : "#e0e0e0",
                  color: "#fff",
                  textTransform: "none",
                  padding: "6px 16px",
                }}
                size="small"
              >
                {sectionSaving.productDetails ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          ) : disableEditing ? null : (
            <Button
              startIcon={<EditIcon />}
              onClick={() => handleEdit(0)}
              className={localClasses.editButton}
              size="small"
              disabled={editingSection !== null} // disabled while another section is being edited
            >
              EDIT
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: "24px" }}>
          {/* isEditMode hides the Deliverable Type field on the Edit Project screen */}
          <ProductDetails {...getStepComponentProps(0, "productDetails")} isEditMode={isEditMode || isEditCampaign} />
        </Box>
      </Paper>

      <Divider style={{ margin: "24px 0" }} />

      {/* Status Selection Section */}
      <Paper elevation={0} style={{ padding: "24px", marginBottom: "24px", border: "1px solid #e0e0e0" }}>
        <Box className={localClasses.sectionHeader}>
          <Typography variant="h6" className={localClasses.sectionTitle}>
            {STEP_NAMES[1]}
          </Typography>
          {editingSection === 1 ? (
            <Box>
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                style={{
                  marginRight: "8px",
                  textTransform: "none",
                  color: "#424242",
                  borderColor: "#e0e0e0",
                  padding: "6px 16px",
                }}
                size="small"
                disabled={sectionSaving.fgStatuses}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSection(1, "fgStatuses")}
                disabled={!sectionHasChanges.fgStatuses || sectionSaving.fgStatuses}
                style={{
                  backgroundColor: sectionHasChanges.fgStatuses ? "#ec6535" : "#e0e0e0",
                  color: "#fff",
                  textTransform: "none",
                  padding: "6px 16px",
                }}
                size="small"
              >
                {sectionSaving.fgStatuses ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          ) : (
            /* Edit Project FSD §5: single EDIT icon governs all 6 sub-panels of
               Status Selection (Project, Campaign, Change Control, Deviation,
               In-Process Test, Product Release Test). StatusSelection is
               toggle-only (no text/date fields), so making it editable in Edit
               Project mode does not expose read-only values. Kept the EDIT
               button visible in Add and Edit modes alike. */
            <Button
              startIcon={<EditIcon />}
              onClick={() => handleEdit(1)}
              className={localClasses.editButton}
              size="small"
              disabled={editingSection !== null} // disabled while another section is being edited
            >
              EDIT
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <StatusSelection {...getStepComponentProps(1, "statusSelection")} />
        </Box>
      </Paper>

      {/*
        Document Deliverables section — per Edit Project FSD §6, displayed
        read-only in Edit Project (previously hidden entirely). The section has
        no EDIT control; every field remains display-only. Kept the wrapping
        fragment so a future revision can toggle it via a single flag.
      */}
      {true && (
      <React.Fragment>
      <Divider style={{ margin: "24px 0" }} />

      {/* Document Deliverables Section */}
      <Paper elevation={0} style={{ padding: "24px", marginBottom: "24px", border: "1px solid #e0e0e0" }}>
        <Box className={localClasses.sectionHeader}>
          <Typography variant="h6" className={localClasses.sectionTitle}>
            {STEP_NAMES[2]}
          </Typography>
          {editingSection === 2 ? (
            <Box>
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                style={{
                  marginRight: "8px",
                  textTransform: "none",
                  color: "#424242",
                  borderColor: "#e0e0e0",
                  padding: "6px 16px",
                }}
                size="small"
                disabled={sectionSaving.documentDeliverables}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSection(2, "documentDeliverables")}
                disabled={!sectionHasChanges.documentDeliverables || sectionSaving.documentDeliverables}
                style={{
                  backgroundColor: sectionHasChanges.documentDeliverables ? "#ec6535" : "#e0e0e0",
                  color: "#fff",
                  textTransform: "none",
                  padding: "6px 16px",
                }}
                size="small"
              >
                {sectionSaving.documentDeliverables ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          ) : disableEditing ? null : (
            <Button
              startIcon={<EditIcon />}
              onClick={() => handleEdit(2)}
              className={localClasses.editButton}
              size="small"
              disabled={editingSection !== null} // disabled while another section is being edited
            >
              EDIT
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <DocumentDeliverables {...getStepComponentProps(2, "documentDeliverables")} />
        </Box>
      </Paper>
      </React.Fragment>
      )}
      {/* end of hidden Document Deliverables section */}

      <Divider style={{ margin: "24px 0" }} />

      {/* Add Campaign ID Section */}
      <Paper ref={campaignSectionRef} elevation={0} style={{ padding: "24px", marginBottom: "24px", border: "1px solid #e0e0e0" }}>
        <Box className={localClasses.sectionHeader}>
          <Typography variant="h6" className={localClasses.sectionTitle}>
            {/* Edit Project FSD §7: section title reads "Campaign Management".
                Add New Project flow keeps its stepper-consistent name. */}
            {(isEditMode || isEditCampaign) ? "Campaign Management" : STEP_NAMES[3]}
          </Typography>
          {editingSection === 3 ? (
            <Box>
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                style={{
                  marginRight: "8px",
                  textTransform: "none",
                  color: "#424242",
                  borderColor: "#e0e0e0",
                  padding: "6px 16px",
                }}
                size="small"
                disabled={sectionSaving.campaignID}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSection(3, "campaignID")}
                disabled={!sectionHasChanges.campaignID || sectionSaving.campaignID}
                style={{
                  backgroundColor: sectionHasChanges.campaignID ? "#ec6535" : "#e0e0e0",
                  color: "#fff",
                  textTransform: "none",
                  padding: "6px 16px",
                }}
                size="small"
              >
                {sectionSaving.campaignID ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          ) : disableEditing ? null : (
            <Button
              startIcon={<EditIcon />}
              onClick={() => handleEdit(3)}
              className={localClasses.editButton}
              size="small"
              disabled={editingSection !== null} // disabled while another section is being edited
            >
              EDIT
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <AddCampaignID
            {...getStepComponentProps(3, "campaignID")}
            // Edit mode: keep every campaign field read-only EXCEPT the Tab &
            // Column configuration, which stays editable inline.
            configOnlyEditable={isEditMode || isEditCampaign}
          />
        </Box>
      </Paper>

      {/* Navigation Buttons - Main page has back button, sections don't */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "80px",
          paddingTop: "24px",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          // TEMP: disabled on the Edit Project / Edit Campaign screens (shown
          // for layout). Add-project "Save Project" stays enabled.
          disabled={saveDisabled}
          className={classes?.button}
          style={{
            // Grey out when disabled so it visually reads as disabled (the
            // inline colour would otherwise override MUI's disabled styling).
            backgroundColor: saveDisabled ? "#e0e0e0" : "#ec6535",
            color: "#fff",
            padding: "10px 32px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            boxShadow: saveDisabled ? "none" : "0 2px 4px rgba(236, 101, 53, 0.3)",
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8, color: "#fff" }} />
              {isEditMode ? "Updating..." : "Submitting..."}
            </>
          ) : (
            isEditMode ? "Update Project" : "Save Project"
          )}
        </Button>
      </Box>

      {/* Full Page Loader */}
      {(isSubmitting || isRedirecting) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}

      {/* Success Popup Modal */}
      <Dialog
        open={showSuccessPopup}
        onClose={handlePopupOk}
        fullWidth
        className="project-added-success-popup"
        maxWidth="sm"
      >
        <StyledDialog
          title={isEditMode ? "Project Updated" : "New Project Added"}
          subtitle={isEditMode
            ? "The project details have been updated successfully."
            : "The project details have been added successfully."}
          icon={false} // false means show success (check), true means error (cross)
          handleClose={handlePopupOk}
          noCloseButton={true}
        />
      </Dialog>

      {/* Error Popup Modal — StyledDialog always shows a success check, so the
          failure case uses a self-contained error dialog instead. */}
      <Dialog
        open={errorPopup.open}
        onClose={handleCloseErrorPopup}
        fullWidth
        className="project-update-error-popup"
        maxWidth="sm"
      >
        <Box style={{ padding: "2rem", textAlign: "center" }}>
          <Typography variant="h5" style={{ fontWeight: 600, color: "#d32f2f", marginBottom: "1rem" }}>
            {isEditMode ? "Update Failed" : "Save Failed"}
          </Typography>
          <Typography variant="body2" style={{ marginBottom: "1.5rem", color: "#424242" }}>
            {errorPopup.message}
          </Typography>
          <Button
            variant="contained"
            onClick={handleCloseErrorPopup}
            style={{
              backgroundColor: "#ec6535",
              color: "#fff",
              padding: "8px 32px",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "2rem",
            }}
          >
            OK
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ReviewAndAddProject;
